import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Download, Gavel, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Judges Sheets (White System) — one judges-sheet table per non-RollArt
 * element across the competition's order of skate. Single-competition UI;
 * gated on the skating order draw being present.
 */
export default function JudgesSheetsPanel({ competitionsWithOOS = [], competitionId = '' }) {
  const compId = competitionId;
  const [preflight, setPreflight] = useState(null);
  const [loadingPreflight, setLoadingPreflight] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!compId) {
      setPreflight(null);
      return;
    }
    setLoadingPreflight(true);
    axios
      .get(`${API}/documents/judges-sheets/${compId}/preflight`, { withCredentials: true })
      .then((res) => setPreflight(res.data))
      .catch((err) => {
        toast.error(err.response?.data?.detail || 'Failed to load competition status');
        setPreflight(null);
      })
      .finally(() => setLoadingPreflight(false));
  }, [compId]);

  const competition = competitionsWithOOS.find((c) => c.competition_id === compId);

  const handleGenerate = async () => {
    if (!compId) {
      toast.error('Select a competition first');
      return;
    }
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/documents/judges-sheets?competition_id=${compId}`,
        {},
        { withCredentials: true, responseType: 'blob' }
      );
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : 'Judges_Sheets.xlsx';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Judges Sheets generated');
    } catch (err) {
      let msg = 'Failed to generate Judges Sheets';
      if (err.response?.data instanceof Blob) {
        try {
          const txt = await err.response.data.text();
          const json = JSON.parse(txt);
          msg = json.detail || msg;
        } catch { /* keep default */ }
      } else if (err.response?.data?.detail) {
        msg = err.response.data.detail;
      }
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const whiteSystemCount = preflight?.n_white_system_events ?? preflight?.n_events ?? 0;
  const hasWhiteSystemEvents = whiteSystemCount > 0;
  const ready =
    !!preflight && preflight.has_oos && preflight.has_draw && hasWhiteSystemEvents;

  return (
    <Card className="p-6 space-y-5" data-testid="judges-sheets-panel">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Gavel className="h-5 w-5 text-[#2D4A3E]" />
          <h3 className="text-lg font-semibold text-[#2D4A3E]">Judges Sheets (White System)</h3>
        </div>
        <p className="text-sm text-stone-600">
          One judges sheet per element for every non-RollArt event — figures, dances, programmes
          with judges' notes, and Competition Specific events. Skating order rotates per element
          using the same rules as the Run Sheet. Styling follows your active document colour scheme.
        </p>
      </div>

      <div>
        <p className="text-sm text-stone-600">
          {!compId ? (
            <>Pick a competition above to continue.</>
          ) : (() => {
            const inOOS = competitionsWithOOS.find((c) => c.competition_id === compId);
            if (!inOOS) {
              return <span className="text-amber-900">This competition doesn't have an Order of Skate yet.</span>;
            }
            return <>Generating Judges Sheets for <span className="font-medium">{inOOS.name}</span>.</>;
          })()}
        </p>
      </div>

      {compId && loadingPreflight && (
        <div className="text-sm text-stone-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking competition status…
        </div>
      )}

      {compId && preflight && !loadingPreflight && (
        <div className="rounded-lg border bg-stone-50 px-3 py-3 text-sm">
          {ready ? (
            <div className="space-y-1">
              <p>
                <span className="font-medium" data-testid="judges-sheets-event-count">
                  {preflight.n_white_system_events ?? preflight.n_events}
                </span>{' '}
                White System event{(preflight.n_white_system_events ?? preflight.n_events) === 1 ? '' : 's'},{' '}
                <span className="font-medium" data-testid="judges-sheets-entry-count">
                  {preflight.n_entry_records ?? preflight.n_skater_records}
                </span>{' '}
                entry record{(preflight.n_entry_records ?? preflight.n_skater_records) === 1 ? '' : 's'} ready.
              </p>
              {preflight.draw_created_at && (
                <p className="text-xs text-stone-500">
                  Skating order draw: {new Date(preflight.draw_created_at).toLocaleString()}
                </p>
              )}
            </div>
          ) : (
            <div className="flex items-start gap-2 text-amber-900">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                {!preflight.has_oos && <p>Order of Skate hasn't been generated yet.</p>}
                {preflight.has_oos && !preflight.has_draw && (
                  <p>Run a Skating Order draw before generating the Judges Sheets.</p>
                )}
                {preflight.has_oos && preflight.has_draw && !hasWhiteSystemEvents && (
                  <p data-testid="judges-sheets-no-white-system">
                    There are no events being judged using the White System in this competition.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div>
        <Button
          className="btn-primary"
          onClick={handleGenerate}
          disabled={!ready || generating}
          data-testid="judges-sheets-generate-btn"
        >
          {generating ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Generating…</>
          ) : (
            <><Download className="mr-2 h-4 w-4" />Generate Judges Sheets</>
          )}
        </Button>
        {competition && (
          <p className="text-xs text-stone-500 mt-2">
            Will download as <span className="font-mono">Judges_Sheets_{(competition.short_name || competition.name || 'Judges').replace(/[^A-Za-z0-9]/g, '_')}.xlsx</span>
          </p>
        )}
      </div>
    </Card>
  );
}
